<template><v-app style="background: #f8fcfb">
  <RouterView />
  <OrderNotificationToast v-if="userStore.isLoggedIn && isSuperAdmin" :notifications="toastNotifications"
    @dismiss="handleToastDismiss" />
</v-app></template>

<script setup lang="ts">
import OrderNotificationToast from '@/components/OrderNotificationToast.vue';
import { useNotifications } from '@/composables/useNotifications';
import { usePermissions } from '@/composables/usePermissions';
import { tokenRefreshService } from '@/services/tokenRefreshService';
import { useUserStore } from '@/stores/user.state';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const userStore = useUserStore();
const route = useRoute();
const { hasRole } = usePermissions();
const { startPolling, stopPolling, notifications, hasNewNotifications } = useNotifications();

// Check if user is SuperAdmin
const isSuperAdmin = computed(() => hasRole('Role.SuperAdmin'));

// Toast notifications management
const toastNotifications = ref<any[]>([]);
const shownToastIds = ref<Set<string>>(new Set());

// Watch for new notifications and show toasts
watch(
  () => notifications.value,
  (newNotifications) => {
    // Show toast for new notifications that haven't been shown yet
    const newToasts = newNotifications.filter(
      (n: any) => !shownToastIds.value.has(n.uuid) && !n.isRead
    );

    newToasts.forEach((notification: any) => {
      shownToastIds.value.add(notification.uuid);
      toastNotifications.value.push({
        ...notification,
        timestamp: Date.now(),
      });
    });
  },
  { deep: true }
);

const handleToastDismiss = (uuid: string) => {
  toastNotifications.value = toastNotifications.value.filter(n => n.uuid !== uuid);
};

// Initialize token refresh service when user is logged in
onMounted(() => {
  if (userStore.isLoggedIn) {
    tokenRefreshService.initialize();

    // Start notification polling for SuperAdmin users
    if (isSuperAdmin.value) {
      startPolling();
    }
  }
});

// Watch for login/logout changes
watch(
  () => userStore.isLoggedIn,
  (isLoggedIn) => {
    if (isLoggedIn) {
      tokenRefreshService.reset();

      // Start polling if SuperAdmin
      if (isSuperAdmin.value) {
        startPolling();
      }
    } else {
      tokenRefreshService.cleanup();
      stopPolling();
      toastNotifications.value = [];
      shownToastIds.value.clear();
    }
  }
);

// Watch for role changes (in case user role changes during session)
watch(
  () => isSuperAdmin.value,
  (isSuperAdmin) => {
    if (isSuperAdmin && userStore.isLoggedIn) {
      startPolling();
    } else {
      stopPolling();
      toastNotifications.value = [];
      shownToastIds.value.clear();
    }
  }
);

// Watch for route changes to record activity
watch(
  () => route.path,
  () => {
    // Route change counts as activity
  }
);

// Cleanup on unmount
onUnmounted(() => {
  tokenRefreshService.cleanup();
  stopPolling();
});
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Montagu+Slab:opsz,wght@16..144,100..700&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Montagu+Slab:opsz,wght@16..144,100..700&family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap");

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Roboto", sans-serif;
}
</style>
